import { dialogService } from '@thefarmersfront/kitchen';

import type { CustomCellRendererProps } from 'ag-grid-react';
import { useShallow } from 'zustand/react/shallow';

import { NumberUnitInput } from '~/components/common';

import { useStockReleaseStore } from '~/store';

import { DEFAULT_INPUT_STATUS, ERROR_STATUS } from '~/constants';

import { TypeStockReleaseGoods } from '~/types/stock.types';

import styles from './StockReleaseRequestQuantityInputCell.module.scss';

export type TypeStockReleaseItemEditableFields = 'availableRequestQuantity' | 'defectiveRequestQuantity';

const fieldLabel: Record<TypeStockReleaseItemEditableFields, string> = {
  availableRequestQuantity: '가용',
  defectiveRequestQuantity: '불용',
};

/**
 * [재고관리 > 반출요청관리] 반출요청등록 모달 > 반출 수량 관련 Input 셀 렌더러
 */
const StockReleaseRequestQuantityInputCell = ({
  node: { rowIndex },
  data,
  column,
}: CustomCellRendererProps<TypeStockReleaseGoods>) => {
  const [requestGoods, requestGoodsState, updateRequestGoods, updateRequestGoodsStatus] = useStockReleaseStore(
    useShallow((state) => [
      state.requestGoods,
      state.requestGoodsState,
      state.actions.updateRequestGoods,
      state.actions.updateRequestGoodsStatus,
    ])
  );

  const colId = column?.getColId() as TypeStockReleaseItemEditableFields;
  const quantityKey = colId.replace('Request', '') as keyof TypeStockReleaseGoods;
  const inventoryQty = Number(data?.[quantityKey] || 0);
  const requestRowData = requestGoods[rowIndex as number];
  const cellData = requestRowData?.[colId] ? Number(requestRowData[colId]) : '';

  if (rowIndex === null || column === undefined || data === undefined) {
    return '-';
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numberValue = Number(e.target.value.trim());

    if (!Number.isInteger(numberValue)) {
      return;
    }

    updateRequestGoods(rowIndex, { [colId]: numberValue });
    updateRequestGoodsStatus(rowIndex, {
      availableRequestQuantity: DEFAULT_INPUT_STATUS,
      defectiveRequestQuantity: DEFAULT_INPUT_STATUS,
    });
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const numberValue = Number(e.target.value.trim());

    if (numberValue > inventoryQty && rowIndex !== null) {
      updateRequestGoodsStatus(rowIndex, { [colId]: ERROR_STATUS });
      dialogService.addDialog({
        children: `반출 요청 수량(${fieldLabel[colId]})은 ${fieldLabel[colId]} 재고보다 같거나 적은 수량만 입력할 수 있습니다.`,
        onConfirm: () => {
          updateRequestGoods(rowIndex, { [colId]: 0 });
        },
      });
      return;
    }
  };

  const handleFocus = () => {
    updateRequestGoodsStatus(rowIndex, {
      availableRequestQuantity: DEFAULT_INPUT_STATUS,
      defectiveRequestQuantity: DEFAULT_INPUT_STATUS,
    });
  };

  return (
    <div className={styles['kitchen-component-cell']}>
      <NumberUnitInput
        type="text"
        size="tiny"
        placeholder="입력"
        maxLength={String(inventoryQty).length}
        disabled={!inventoryQty}
        status={requestGoodsState[rowIndex as number]?.[colId].status ?? 'default'}
        value={cellData}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
    </div>
  );
};

export default StockReleaseRequestQuantityInputCell;
